import React, { useState } from 'react'
import {
  Form,
  Input,
  Button,
  Card,
  Select,
  DatePicker,
  message,
  Space,
  Typography,
  AutoComplete,
} from 'antd'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import caseApi from '../../api/cases'

const { Title, Paragraph } = Typography
const { Option } = Select

const LITIGATION_STAGES = [
  { value: 'first_instance', label: '一审' },
  { value: 'second_instance', label: '二审' },
  { value: 'retrial', label: '再审' },
  { value: 'protest', label: '抗诉' },
]

const EXECUTION_STAGES = [
  { value: 'execution_application', label: '申请执行' },
  { value: 'execution_acceptance', label: '执行立案' },
  { value: 'execution_implementation', label: '执行实施' },
  { value: 'execution_objection', label: '执行异议' },
  { value: 'execution_reconsideration', label: '执行复议' },
  { value: 'execution_supervision', label: '执行监督' },
]

const getStageOptions = (caseType: string) =>
  caseType === 'execution' ? EXECUTION_STAGES : LITIGATION_STAGES

const CaseForm: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const selectedCaseType = Form.useWatch('case_type', form)

  const onFinish = async (values: any) => {
    setLoading(true)
    try {
      const data = {
        ...values,
        decision_date: values.decision_date.format('YYYY-MM-DD'),
      }
      const result = await caseApi.create(data)
      message.success('案件创建成功')
      navigate(`/guidance/${result.id}`)
    } catch (error: any) {
      message.error(error.response?.data?.detail || '创建失败')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 20px' }}>
      <Card>
        <div style={{ textAlign: 'center', marginBottom: 30 }}>
          <Title level={2}>案件信息录入</Title>
          <Paragraph type="secondary">
            请填写您的案件基本信息，系统将为您推荐相应的救济途径
          </Paragraph>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          size="large"
        >
          <Form.Item
            label="案件类型"
            name="case_type"
            rules={[{ required: true, message: '请选择案件类型' }]}
          >
            <Select placeholder="请选择案件类型">
              <Option value="civil">民事案件</Option>
              <Option value="criminal">刑事案件</Option>
              <Option value="administrative">行政案件</Option>
              <Option value="execution">执行案件</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="当前诉讼阶段"
            name="current_stage"
            rules={[{ required: true, message: '请输入或选择当前阶段' }]}
          >
            <AutoComplete
              placeholder="可选择或手动输入当前阶段（如：执行异议）"
              options={getStageOptions(selectedCaseType)}
              allowClear
              filterOption={(inputValue, option) =>
                (option?.label as string)?.toLowerCase().includes(inputValue.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            label="裁判结果"
            name="stage_result"
            rules={[{ required: true, message: '请输入裁判结果' }]}
          >
            <AutoComplete
              placeholder="可选择或手动输入裁判结果（如：执行完毕、终结本次执行）"
              allowClear
              filterOption={(inputValue, option) =>
                (option?.label as string)?.toLowerCase().includes(inputValue.toLowerCase())
              }
              options={
                selectedCaseType === 'execution'
                  ? [
                      { value: '执行完毕', label: '执行完毕' },
                      { value: '终结本次执行', label: '终结本次执行' },
                      { value: '终结执行', label: '终结执行' },
                      { value: '驳回执行申请', label: '驳回执行申请' },
                      { value: '执行异议成立', label: '执行异议成立' },
                    ]
                  : [
                      { value: '判决', label: '判决' },
                      { value: '裁定驳回', label: '裁定驳回' },
                      { value: '裁定不予受理', label: '裁定不予受理' },
                      { value: '调解', label: '调解' },
                      { value: '驳回再审申请', label: '驳回再审申请' },
                    ]
              }
            />
          </Form.Item>

          <Form.Item
            label="裁判文书送达日期"
            name="decision_date"
            rules={[{ required: true, message: '请选择送达日期' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              disabledDate={(current) => current && current > dayjs()}
            />
          </Form.Item>

          <Form.Item
            label="案件描述（可选）"
            name="case_description"
          >
            <Input.TextArea
              rows={4}
              placeholder="请简要描述案件情况"
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button onClick={() => navigate('/')}>
                返回
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                提交并获取引导
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card style={{ marginTop: 20, background: '#f6ffed', borderColor: '#b7eb8f' }}>
        <Paragraph style={{ margin: 0 }}>
          <strong>温馨提示：</strong>系统将根据您填写的案件信息，依据《刑事诉讼法》、《民事诉讼法》、《行政诉讼法》等法律规定，
          自动匹配相应的救济途径并告知申请期限。
        </Paragraph>
      </Card>
    </div>
  )
}

export default CaseForm
